// The Inbox on the screen: what waits for the person (GET /api/me/inbox, the
// same things the app's badge counts) and the settled items of "your turn"
// (GET /api/me/agents/turn/history), read page by page.
//
// What waits sits under the badge's key (`["me", "badge"]`): every event that
// reads the badge again (notices part, conversations) reads it again too, so
// the Inbox and the badge never drift apart. The settled items are pages read
// as the list is scrolled (all of them each time it is read again): they sit
// apart and are read again only when a turn item or a chat changes, not on
// every entry or message the badge follows: an item settling, a chat renamed
// or deleted (part.tsx). Both are read again while the Inbox is not shown too
// (a row opened and settled is gone on coming back), once for a burst.
import {
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import type { AgentTurnHistory } from "@trameria/core/agent-chat-shared";
import type { InboxEntry } from "@trameria/core/notices-shared";
import type { Api } from "../data/api";
import { waitingPrefix } from "../notices/queries";
import { invalidateAfterBurst } from "../realtime/settle";

export const inboxNowQuery = (api: Api) =>
  queryOptions({
    queryKey: [...waitingPrefix, "now"] as const,
    queryFn: async () =>
      (await api<{ items: InboxEntry[] }>("/api/me/inbox")).items,
  });

export interface HistoryFilter {
  q?: string;
  chat: string | null;
  case: string | null;
}

const historyPrefix = ["me", "inbox", "done"] as const;

/** A turn item settled or a chat changed (its title, gone): the settled items. */
export const historyChanged = (qc: QueryClient) =>
  invalidateAfterBurst(qc, { queryKey: historyPrefix, refetchType: "all" });

export const inboxHistoryQuery = (api: Api, filter: HistoryFilter) =>
  infiniteQueryOptions({
    queryKey: [
      ...historyPrefix,
      filter.q ?? "",
      filter.chat ?? "",
      filter.case ?? "",
    ] as const,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filter.q) params.set("q", filter.q);
      if (filter.chat) params.set("chat", filter.chat);
      if (filter.case) params.set("case", filter.case);
      if (pageParam) params.set("cursor", pageParam);
      return api<AgentTurnHistory>(`/api/me/agents/turn/history?${params}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    placeholderData: keepPreviousData,
  });
